import React from 'react'
import { COLORS } from '@renderer/config/colors'

interface PrimaryButtonProps {
  icon: React.ReactNode
  label: string
  onClick: () => void
  isActive?: boolean
  isCollapsed?: boolean
  className?: string
  ariaLabel?: string
  ariaExpanded?: boolean
}

export const PrimaryButton = ({
  icon,
  label,
  onClick,
  isActive = false,
  isCollapsed = false,
  className = '',
  ariaLabel,
  ariaExpanded
}: PrimaryButtonProps) => {
  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

  const buttonStyle = {
    '--nav-bg': isActive ? COLORS.primary.base : COLORS.secondary.background,
    '--nav-bg-hover': isActive ? COLORS.primary.hover : COLORS.secondary.hover,
    '--nav-bg-pressed': isActive ? COLORS.primary.pressed : COLORS.secondary.pressed,
    color: isActive ? COLORS.text.primary : COLORS.secondary.text,
    width: isCollapsed ? '2.75rem' : '15rem',
    padding: isCollapsed ? '0.75rem' : '0.625rem 0.75rem',
    transition: `width 700ms ${EASING}, padding 700ms ${EASING}, background-color 150ms ease, color 150ms ease`
  } as React.CSSProperties

  return (
    <button
      onClick={onClick}
      title={isCollapsed ? (ariaLabel ?? label) : undefined}
      aria-label={ariaLabel ?? label}
      aria-current={isActive ? 'page' : undefined}
      aria-expanded={ariaExpanded}
      className={`flex items-center justify-start gap-3 h-11 overflow-hidden border-none text-[15px] cursor-pointer rounded-xl
        bg-[var(--nav-bg)] hover:bg-[var(--nav-bg-hover)] active:bg-[var(--nav-bg-pressed)]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7a6020]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white
        ${isActive ? 'font-medium shadow-sm shadow-[#7a6020]/20' : 'font-normal'} ${className}`}
      style={buttonStyle}
    >
      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">{icon}</div>
      <span
        aria-hidden="true"
        className="whitespace-nowrap overflow-hidden"
        style={{
          opacity: isCollapsed ? 0 : 1,
          transition: isCollapsed ? 'opacity 150ms ease-in' : `opacity 300ms 400ms ease-out`
        }}
      >
        {label}
      </span>
    </button>
  )
}
